import {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';

import {
  CarouselArrowsType,
  MentorRequestContextType,
  MentorRequestModalSettings,
} from 'redux/schemas/models/mentor-request';

import { css } from '@emotion/react';
import { screenSmMin } from 'styles/global_defaults/media-queries';
import { quarterSpacing, standardSpacing } from 'styles/global_defaults/scaffolding';

import MentorRequestInfo from './mentor-request-info';
import MentorRequestGoBack from './mentor-request-go-back';
import CarouselControls from './mentor-request-carousel/carousel-controls';
import MentorRequestParticipantInfo from './mentor-request-participant-info';
import { MentorRequestProvider } from './mentor-request-context';
import { primary } from 'styles/global_defaults/colors';

const styles = css`
  .header-line {
    border-top: ${quarterSpacing}px solid ${primary};
  }
  .modal-page {
    display: flex;
    flex-direction: column;
  }
  .modal-panel {
    overflow: auto;
    max-height: 100%;
    min-width: 300px;
    scrollbar-width: none;
    ::-webkit-scrollbar {
      display: none;
    }
    &.connection-info {
      max-width: 380px;
    }
  }
  .mentor-request-modal-content {
    overflow: hidden;
    align-items: center;
    justify-content: center;
  }
  @media (max-width: ${screenSmMin}px) {
    .mentor-request-modal-content {
      flex-direction: column;
      overflow: auto;
      align-items: unset;
      justify-content: unset;
      .modal-panel {
        margin: 0 !important;
        width: 100% !important;
        max-width: unset;
        padding: 0 !important;
        padding-bottom: ${standardSpacing}px !important;
        overflow: visible;
        max-height: unset;
        scrollbar-width: unset;
        ::-webkit-scrollbar {
          display: unset;
        }
        &.connection-info {
          img {
            width: 100% !important;
          }
        }
      }
    }
  }
`;

type MentorRequestHandle = {
  canDiscardChanges: boolean;
  setCanDiscardChanges: null | ((canDiscard: boolean) => void);
  resetForm: null | (() => void);
};

type MentorRequestContentProps = MentorRequestModalSettings & {
  isLoading?: boolean;
};

const MentorRequestContent = forwardRef((props: MentorRequestContentProps, ref) => {
  const {
    source,
    showGoBack,
    showCarousel,
    hasLeftItems,
    hasRightItems,
    getPreviousMentorRequest,
    getNextMentorRequest,
    request,
    requests,
    participant,
    isLoading,
  } = props;

  const [canDiscardChanges, setCanDiscardChanges] = useState(true);
  const [resetForm, setResetForm] = useState(null);

  const carouselArrows: CarouselArrowsType = {
    left: {
      show: hasLeftItems,
      onClick: getPreviousMentorRequest,
    },
    right: {
      show: hasRightItems,
      onClick: getNextMentorRequest,
    },
  };

  const mentorRequestValue: MentorRequestContextType = useMemo(() => ({
    source,
    request,
    requests,
    participant,
    canDiscardChanges,
    setCanDiscardChanges,
    resetForm,
    setResetForm,
  }), [
    source,
    request,
    requests,
    participant,
    canDiscardChanges,
    setCanDiscardChanges,
    resetForm,
    setResetForm,
  ]);

  useImperativeHandle(ref, () => ({
    canDiscardChanges,
    setCanDiscardChanges,
    resetForm,
  } as MentorRequestHandle), [
    canDiscardChanges,
    setCanDiscardChanges,
    resetForm,
  ]);

  return (
    <div css={styles}>
      <MentorRequestProvider value={mentorRequestValue}>
        {showGoBack && <MentorRequestGoBack />}
        {showCarousel && <CarouselControls {...carouselArrows} />}
        <div className='modal-page'>
          <div className='header-line' />
          <div className='d-flex w-100 h-100 px-8 mentor-request-modal-content'>
            {!isLoading && participant?.id && (
              <>
                <MentorRequestInfo />
                <MentorRequestParticipantInfo />
              </>
            )}
          </div>
        </div>
      </MentorRequestProvider>
    </div>
  );
});

export default MentorRequestContent;
